import {Form, Select} from 'antd'
import React, {useMemo} from 'react'
import {useIntl} from 'react-intl'
import dayjs from 'dayjs'
import weekday from 'dayjs/plugin/weekday'
import {CustomToDoType, PredefinedToDoTypes, ToDoTypeMetadata} from '../../entities/journalRecord'
import {EmEmoji} from '../emoji/EmEmoji'
import {DateRangePicker, DateRangePreset} from '../ui/dateRangePicker/DateRangePicker'
import type {ToDoFilters} from './ToDoPageFilters'

dayjs.extend(weekday)

export type ToDoPageFiltersSmallProps = {
  plantedPlantsNamesWithId: {id: number; name: string}[]
  userToDoTypes: CustomToDoType[]
  filters: ToDoFilters
  setFilters: (filters: ToDoFilters) => void
}

// The filters are stored as objects keyed by type/plant id, so the selection
// always travels as the string keys of those objects.
function toSelectionMap(values: string[]) {
  return Object.fromEntries(values.map((value) => [value, true]))
}

// The wide layout spreads three checkbox columns and an always-open calendar
// across the panel; none of that fits a phone, so the same filters are edited
// through stacked multiple-choice selects and a narrow date range picker.
export function ToDoPageFiltersSmall({
  plantedPlantsNamesWithId,
  userToDoTypes,
  filters,
  setFilters,
}: ToDoPageFiltersSmallProps) {
  const intl = useIntl()

  const typeOptions = useMemo(
    () => [
      ...PredefinedToDoTypes.map((type) => ({
        value: type as string,
        label: intl.formatMessage({id: `eventType-${type}`}),
        icon: ToDoTypeMetadata[type].icon,
      })),
      ...userToDoTypes.flatMap(({customToDoType}) =>
        customToDoType
          ? [
              {
                value: String(customToDoType.id),
                label: customToDoType.name,
                icon: customToDoType.icon,
              },
            ]
          : []
      ),
    ],
    [userToDoTypes, intl]
  )

  const typeIcons = useMemo(
    () => Object.fromEntries(typeOptions.map((option) => [option.value, option.icon])),
    [typeOptions]
  )

  const plantOptions = useMemo(
    () => plantedPlantsNamesWithId.map(({id, name}) => ({value: String(id), label: name})),
    [plantedPlantsNamesWithId]
  )

  const datePresets: DateRangePreset[] = [
    {label: intl.formatMessage({id: 'today'}), value: [dayjs(), dayjs()]},
    {label: intl.formatMessage({id: 'thisWeek'}), value: [dayjs().weekday(0), dayjs().weekday(6)]},
    {
      label: intl.formatMessage({id: 'thisMonth'}),
      value: [dayjs().startOf('month'), dayjs().endOf('month')],
    },
  ]

  return (
    <Form className="todo__filters-small" layout="vertical">
      <Form.Item label={intl.formatMessage({id: 'taskTypes'})}>
        <Select
          mode="multiple"
          allowClear
          placeholder={intl.formatMessage({id: 'chooseType'})}
          value={Object.keys(filters.types)}
          options={typeOptions}
          optionFilterProp="label"
          optionRender={(option) => (
            <span className="todo__filters-small__type-item">
              <EmEmoji id={option.data.icon} size="1em" /> {option.data.label}
            </span>
          )}
          labelRender={({label, value}) => (
            <span className="todo__filters-small__type-item">
              <EmEmoji id={typeIcons[value as string]} size="1em" /> {label}
            </span>
          )}
          onChange={(values: string[]) => setFilters({...filters, types: toSelectionMap(values)})}
        />
      </Form.Item>

      <Form.Item label={intl.formatMessage({id: 'plants'})}>
        <Select
          mode="multiple"
          allowClear
          placeholder={intl.formatMessage({id: 'plants'})}
          value={Object.keys(filters.plants)}
          options={plantOptions}
          optionFilterProp="label"
          onChange={(values: string[]) => setFilters({...filters, plants: toSelectionMap(values)})}
        />
      </Form.Item>

      <Form.Item label={intl.formatMessage({id: 'date'})}>
        <DateRangePicker
          value={[
            filters.startDate ? dayjs(filters.startDate) : null,
            filters.endDate ? dayjs(filters.endDate) : null,
          ]}
          presets={datePresets}
          onChange={(dates) =>
            setFilters({
              ...filters,
              startDate: dates?.[0]?.toDate() || undefined,
              endDate: dates?.[1]?.toDate() || undefined,
            })
          }
        />
      </Form.Item>
    </Form>
  )
}
